import { cx } from "../../lib/cx.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { useControllable } from "../../lib/useControllable.js";
import { FormFieldContext } from "../FormField/FormFieldContext.js";
import { Input } from "../Input/Input.js";
/* empty css                */
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/SearchInput/SearchInput.tsx
/**
* A search box: an Input with a search icon and, once there's text, a clear button.
* Escape clears it too; focus returns to the field and the clearing is announced.
*/
var SearchInput = forwardRef(function SearchInput({ value: valueProp, defaultValue = "", onValueChange, onClear, debounce, label = "Search", clearLabel = "Clear search", clearedMessage = "Search cleared", onChange, onKeyDown, disabled, readOnly, className, "aria-label": ariaLabel, ...rest }, ref) {
	const inField = useContext(FormFieldContext) !== null;
	const inputRef = useRef(null);
	useImperativeHandle(ref, () => inputRef.current);
	const [value, setValue] = useControllable(valueProp, defaultValue);
	const [announcement, setAnnouncement] = useState("");
	const timer = useRef(void 0);
	useEffect(() => () => clearTimeout(timer.current), []);
	const report = (next, immediate) => {
		clearTimeout(timer.current);
		if (!onValueChange) return;
		if (immediate || !debounce) onValueChange(next);
		else timer.current = setTimeout(() => onValueChange(next), debounce);
	};
	const handleChange = (event) => {
		onChange?.(event);
		setValue(event.target.value);
		setAnnouncement("");
		report(event.target.value, false);
	};
	const clear = () => {
		setValue("");
		report("", true);
		onClear?.();
		setAnnouncement(clearedMessage);
		inputRef.current?.focus();
	};
	const handleKeyDown = (event) => {
		onKeyDown?.(event);
		if (event.defaultPrevented) return;
		if (event.key === "Escape" && value !== "" && !readOnly) {
			event.preventDefault();
			event.stopPropagation();
			clear();
		}
	};
	const canClear = value !== "" && !disabled && !readOnly;
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx(Input, {
		...rest,
		ref: inputRef,
		type: "search",
		"aria-label": ariaLabel ?? (inField ? void 0 : label),
		value,
		onChange: handleChange,
		onKeyDown: handleKeyDown,
		disabled,
		readOnly,
		className: cx("ds-search-input", className),
		leading: /* @__PURE__ */ jsx(Icon, { name: "search" }),
		trailing: canClear ? /* @__PURE__ */ jsx(IconButton, {
			icon: "x",
			size: "sm",
			className: "ds-search-input__clear",
			"aria-label": clearLabel,
			onClick: clear
		}) : void 0
	}), /* @__PURE__ */ jsx("span", {
		className: "ds-search-input__status",
		role: "status",
		children: announcement
	})] });
});
//#endregion
export { SearchInput };

//# sourceMappingURL=SearchInput.js.map